import { CurrencyType } from "../enums";
import { CurrencyTypeSymbols } from "../labels";

/**
 * Currency formatting for the app.
 *
 * The app is EUR-only: every price stored from 2026 onwards is in EUR and is
 * displayed in EUR, with no second-currency equivalent next to it.
 *
 * The single exception is historical data. Payments recorded before the euro
 * switch are stored with `CurrencyId = CurrencyType.BGN` and their raw BGN
 * amount — those rows are displayed in BGN, exactly as they were paid. They are
 * never converted for display; conversion happens only on the backend
 * (`CurrencyUtility.NormalizePriceToEur`) and only so that mixed-currency rows
 * can be summed into a single EUR total.
 *
 * If a second currency ever has to be displayed again (e.g. a new market with a
 * transition period), see docs/currency.md — the whole feature is meant to come
 * back through `formatPrice` and `PriceDisplay`, not through 40 call sites.
 */

/** Formats an amount that is already known to be in EUR. */
export const formatEurValue = (eurValue: number): string => {
  return `€ ${eurValue.toFixed(2)}`;
};

/**
 * Signed variant of {@link formatEurValue}: positive values carry an explicit
 * leading "+" (deposit balances / ledger rows, where the sign is meaningful).
 */
export const formatSignedEurValue = (eurValue: number): string => {
  const formatted = formatEurValue(eurValue);
  return eurValue > 0 ? `+${formatted}` : formatted;
};

/**
 * Formats an amount in the currency it was actually recorded in.
 *
 * Anything that is not an explicit legacy BGN payment is treated as EUR — that
 * covers `undefined`, `null`, `0` (the pre-migration unset value) and any
 * unknown id.
 */
export const formatPrice = (
  amount: number,
  currencyId?: number | null,
): string => {
  if (currencyId === CurrencyType.BGN) {
    return `${amount.toFixed(2)} ${CurrencyTypeSymbols[CurrencyType.BGN]}`;
  }

  return formatEurValue(amount);
};

/**
 * Signed variant of {@link formatPrice}: positive values carry an explicit
 * leading "+" (deposit ledger rows / balances, where the sign is meaningful).
 */
export const formatSignedPrice = (
  amount: number,
  currencyId?: number | null,
): string => {
  const formatted = formatPrice(amount, currencyId);
  return amount > 0 ? `+${formatted}` : formatted;
};

export type AmountTone = "positive" | "negative" | "neutral";

/**
 * Presentation tone for a signed amount: green above zero, red below,
 * gray/neutral at exactly zero.
 */
export const getAmountTone = (value: number): AmountTone => {
  if (value > 0) {
    return "positive";
  }
  if (value < 0) {
    return "negative";
  }
  return "neutral";
};
